import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { Button as FcButton } from '../ui';

const NotificationPermissionModal = ({ isOpen, onClose, onAllow }) => {
  const [isRequesting, setIsRequesting] = useState(false);

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleEnableNotifications = async () => {
    setIsRequesting(true);
    
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // For now, just close the modal and handle registration separately
        if (onAllow) onAllow();
        handleCloseModal();
        
        // Show a test notification
        new Notification('🎉 Notifications Enabled!', {
          body: 'You\'ll now receive team selection reminders.',
          icon: '/icon-192.png'
        });
      } else {
        // User denied - close modal and show instructions
        handleCloseModal();
        alert('Please enable notifications in your browser settings to receive team selection reminders.');
      }
    } catch (error) {
      handleCloseModal();
      alert('There was an error requesting notification permission. Please enable manually in browser settings.');
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal} title="">
      <div className="space-y-6 text-center">
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gradient-to-br from-brand-accent-from to-brand-accent-to p-4 rounded-full">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.73 21a2 2 0 01-3.46 0" />
            </svg>
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-brand-text mb-4">
            Never Miss Team Selection Deadlines
          </h3>
          <p className="text-brand-text-muted text-lg mb-6">
            Get timely reminders when you haven't picked your team yet. We'll notify you:
          </p>
        </div>

        <div className="space-y-4 text-left bg-brand-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-brand-text font-medium">
              6 hours before the deadline
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-3 h-3 bg-amber-500 rounded-full"></div>
            <span className="text-brand-text font-medium">
              3 hours before the deadline (final reminder)
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-3 h-3 bg-brand-accent-from rounded-full"></div>
            <span className="text-brand-text font-medium">
              Only when you haven't made a selection
            </span>
          </div>
        </div>

        <div className="bg-brand-dark p-4 rounded-lg border border-brand-border">
          <div className="flex items-center justify-center space-x-2 text-brand-text-muted">
            <span className="text-2xl">💡</span>
            <span className="text-sm">
              You can disable notifications anytime in your browser settings
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <FcButton variant="secondary" type="submit"
            onClick={handleCloseModal}
            className="flex-1 border"
          >
            Maybe Later
          </FcButton>
          <FcButton variant="tertiary" type="submit" aria-label="Close"
            onClick={handleEnableNotifications}
            disabled={isRequesting}
            className="flex-1 from-brand-accent-from to-brand-accent-to disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isRequesting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enabling...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
                Enable Notifications
              </>
            )}
          </FcButton>
        </div>
      </div>
    </Modal>
  );
};

export default NotificationPermissionModal;
